
























































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {SelectOption} from "@/util/selectOption";
import {BalanceEntryType, RecurrenceType, RecurringTransactionData} from "@/util/transactionData";
import spacetime from "spacetime";
import axios from 'axios';

type OperationType = 'future' | 'all';

@Component
export default class RecurringBalanceEntry extends Vue {
    @Prop()
    descriptionOptions!: SelectOption[];

    @Prop()
    recurringEntryData!: RecurringTransactionData;

    private errors: any = {
        amountError: null,
        noteError: null,
        recurrenceEndError: null,
    };

    private otherError: string | null = null;

    private warnings: any = {
        amountWarning: null,
    };

    private amount: number = this.recurringEntryData.amount;
    private note: string | null = this.recurringEntryData.note;
    private remainingOccurrences: number | null = this.recurringEntryData.remainingOccurrences;
    private recurrenceEndType: 'never' | 'numTimes' = this.recurringEntryData.remainingOccurrences ? 'numTimes' : 'never';
    private updateType: OperationType = 'future';
    private deleteType: OperationType = 'future';

    private isUpdatingEntry: boolean = false;
    private isDeletingEntry: boolean = false;

    private get titleForModals(): string {
        return this.recurringEntryData.type === BalanceEntryType.INCOME ? 'Recurring Income' : 'Recurring Expense';
    }

    private get recurrenceType(): string {
        switch (this.recurringEntryData.recurrenceType) {
            case RecurrenceType.WEEKLY:
                return 'Weekly';
            case RecurrenceType.MONTHLY:
                return 'Monthly';
        }
    }

    private get occurrenceLabel(): string {
        if (this.recurringEntryData.isActive) {
            return 'Next occurrence';
        } else {
            return 'Last occurrence';
        }
    }

    private get occurrenceValue(): string {
        if (this.recurringEntryData.isActive && this.recurringEntryData.nextOccurrenceDate) {
            return spacetime(this.recurringEntryData.nextOccurrenceDate).format('numeric-uk').toString();
        } else if (this.recurringEntryData.lastOccurrenceDate) {
            return spacetime(this.recurringEntryData.lastOccurrenceDate).format('numeric-uk').toString();
        }

        return '';
    }

    private get canUpdateEntry(): boolean {
        const hasErrors = Object.keys(this.errors).filter(key => this.errors[key] !== null).length > 0;
        const amountValid = this.amount > 0;

        return !hasErrors && amountValid;
    }

    @Watch('amount')
    private onAmountChanged(newAmount: number) {
        if (newAmount >= 1000) {
            this.warnings.amountWarning = `You've entered a very high amount. Please double check before submitting`;
        } else {
            this.warnings.amountWarning = null;
        }
    }

    @Watch('note')
    private noteUpdated(value: string) {
        this.errors.noteError = null;
    }

    @Watch('recurrenceEndType')
    private onRecurrenceEndTypeUpdated(newEndType: string) {
        if (newEndType === 'never') {
            this.errors.recurrenceEndError = null;
            this.remainingOccurrences = this.recurringEntryData.remainingOccurrences;
        }
    }

    @Watch('remainingOccurrences')
    private onRemainingOccurrencesUpdated(times: number) {
        this.errors.recurrenceEndError = null;
        if (times < 1 || times > 100) {
            this.errors.recurrenceEndError = 'Please pick a number between 1 and 100';
        }
    }

    private updateEntry() {
        if (this.isUpdatingEntry && !this.canUpdateEntry) {
            return;
        }
        this.isUpdatingEntry = true;

        axios.put(`/recurringBalances/${this.recurringEntryData.id}`, {
            'note': this.note?.trim() || null,
            'amount': this.amount,
            'operationType': this.updateType,
            'recurrenceEnd': this.recurrenceEndType === 'numTimes' ? this.remainingOccurrences : null,
        }).then(() => {
            this.reloadBalances();
        }).catch(error => {
            const data = error.response.data;
            const errors = data.errors;
            if (data.message) {
                this.otherError = data.message;
            }
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const errorKey = `${key}Error`;
                    if (this.errors.hasOwnProperty(errorKey)) {
                        this.errors[errorKey] = errors[key][0];
                    }
                });
            }
        }).finally(() => {
            this.isUpdatingEntry = false;
        })
    }

    private deleteEntry() {
        if (this.isDeletingEntry) {
            return;
        }
        this.isDeletingEntry = true;

        axios.delete(`/recurringBalances/${this.recurringEntryData.id}?operationType=${this.deleteType}`)
            .then(() => {
                this.reloadBalances();
            }).catch(error => {
            const data = error.response.data;
            if (data.message) {
                this.otherError = data.message;
            }
        }).finally(() => {
            this.isDeletingEntry = false;
        })
    }

    private reloadBalances() {
        const hash = this.recurringEntryData.type === BalanceEntryType.INCOME ? 'income' : 'expense';
        const currentPath = window.location.pathname;
        window.location.href = `/balance#${hash}`;
        if (currentPath === '/balance') {
            window.location.reload();
        }
    }
}
